import type {Uuid} from "@vesta/common-type-definitions/typeDefinitions";
import {uuidZodValidator} from "@vesta/global-common-typescript/validators/zodValidators";
import {B2bQuotationEngineWorkspaceCapabilityUdoTemplateKey} from "@vesta/vos-types/modules/workspaceLevel/workspaceCapabilities/b2bQuotationEngineWorkspaceCapability/domain/B2bQuotationEngineWorkspaceCapabilityUdoTemplateKey";
import type {WorkspaceCapabilityType} from "@vesta/vos-types/modules/workspaceLevel/workspaceCapabilities/workspaceCapabilities/domain/WorkspaceCapabilityType";
import type {WorkspaceCapabilityUdoTemplateConfiguration} from "@vesta/vos-types/modules/workspaceLevel/workspaceCapabilities/workspaceCapabilities/domain/WorkspaceCapabilityUdoTemplateConfiguration";
import {z as zod} from "zod";

export type B2bQuotationEngineWorkspaceCapabilityUdoBindings = WorkspaceCapabilityUdoTemplateConfiguration<WorkspaceCapabilityType.b2bQuotationEngine> & {
    fixedUdoTemplates: {
        [B2bQuotationEngineWorkspaceCapabilityUdoTemplateKey.quotation]: {
            udoAdditionalColumnIds: {
                adminCommercialDetails: Uuid;
                adminNotes: Uuid;
                convertedCartId: Uuid;
                customerNotes: Uuid;
                generatedBy: Uuid;
                ottokartStorefrontCustomerId: Uuid;
                quoteNumber: Uuid;
                rejectionReason: Uuid;
                sharedBy: Uuid;
                sourceQuotationId: Uuid;
                status: Uuid;
                totalAmount: Uuid;
                validUntil: Uuid;
            };
        };
        [B2bQuotationEngineWorkspaceCapabilityUdoTemplateKey.lineItem]: {
            udoAdditionalColumnIds: {
                approvedPrice: Uuid;
                compatibleMachines: Uuid;
                discountPercent: Uuid;
                isCustom: Uuid;
                marketPrice: Uuid;
                productCatalogueId: Uuid;
                productNameOverride: Uuid;
                quantity: Uuid;
                quotationId: Uuid;
                requestedPrice: Uuid;
                skuOverride: Uuid;
                sortOrder: Uuid;
                status: Uuid;
            };
        };
    };
};

export const b2bQuotationEngineWorkspaceCapabilityUdoBindingsValidator = zod.strictObject({
    fixedUdoTemplates: zod.strictObject({
        [B2bQuotationEngineWorkspaceCapabilityUdoTemplateKey.quotation]: zod.strictObject({
            udoTemplateId: uuidZodValidator,
            udoAdditionalColumnIds: zod.strictObject({
                status: uuidZodValidator,
                quoteNumber: uuidZodValidator,
                ottokartStorefrontCustomerId: uuidZodValidator,
                totalAmount: uuidZodValidator,
                validUntil: uuidZodValidator,
                customerNotes: uuidZodValidator,
                adminNotes: uuidZodValidator,
                rejectionReason: uuidZodValidator,
                convertedCartId: uuidZodValidator,
                sourceQuotationId: uuidZodValidator,
                generatedBy: uuidZodValidator,
                sharedBy: uuidZodValidator,
                adminCommercialDetails: uuidZodValidator,
            }),
        }),
        [B2bQuotationEngineWorkspaceCapabilityUdoTemplateKey.lineItem]: zod.strictObject({
            udoTemplateId: uuidZodValidator,
            udoAdditionalColumnIds: zod.strictObject({
                status: uuidZodValidator,
                quotationId: uuidZodValidator,
                productCatalogueId: uuidZodValidator,
                quantity: uuidZodValidator,
                requestedPrice: uuidZodValidator,
                approvedPrice: uuidZodValidator,
                marketPrice: uuidZodValidator,
                discountPercent: uuidZodValidator,
                compatibleMachines: uuidZodValidator,
                productNameOverride: uuidZodValidator,
                skuOverride: uuidZodValidator,
                sortOrder: uuidZodValidator,
                isCustom: uuidZodValidator,
            }),
        }),
    }),
}) satisfies zod.ZodType<B2bQuotationEngineWorkspaceCapabilityUdoBindings>;
